import { InboxOutlined } from "@ant-design/icons";
import { faCheckCircle, faFileCircleMinus, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { TableColumnsType } from "antd";
import { Button, Select, Tooltip, Typography, Upload } from "antd";
import { RcFile } from "antd/es/upload";
import { showError } from "common/ToastNotifications";
import LoadingOverlay from "components/atoms/LoadingOverlay";
import Papaparse from "papaparse/papaparse";
import React from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";
import DataTable from "../DataTable";
import { IFileDraggerProps } from "./types";

const { Dragger } = Upload;

const removeTrailingEmptyRow = (resultData: PapaparseResult["data"]) => {
    if (resultData && resultData.length > 0) {
        const lastRow = resultData[resultData.length - 1];
        const lastRowValues = Object.values(lastRow);
        if (!lastRowValues.some((value) => !value === false)) {
            resultData.splice(resultData.length - 1, 1);
        }
    }
};

const FileDragger = React.forwardRef(
    (
        {
            hasHeader = true,
            disabled = false,
            showPreview = true,
            enableColumnMapping = false,
            onCancelUpload,
            onReadingFile,
            onReadFileComplete,
            onReadFileError,
            dataSchemaColumns = [],
        }: IFileDraggerProps,
        ref,
    ) => {
        const [file, setFile] = React.useState<null | RcFile>(null);
        const [data, setData] = React.useState<null | PapaparseDataNoHeader | PapaparseDataWithHeader>(null);
        const [columns, setColumns] = React.useState<null | string[]>(null);
        const [isReadingFile, setIsReadingFile] = React.useState<boolean>(false);

        // Column mapping state — only used when enableColumnMapping=true
        const [rawData, setRawData] = React.useState<null | PapaparseDataWithHeader>(null);
        const [fullRawData, setFullRawData] = React.useState<null | PapaparseDataWithHeader>(null);
        const [rawColumns, setRawColumns] = React.useState<null | string[]>(null);
        const [originalFile, setOriginalFile] = React.useState<null | RcFile>(null);
        const [keyCol, setKeyCol] = React.useState<null | string>(null);
        const [descCol, setDescCol] = React.useState<null | string>(null);

        const previewColumns = React.useMemo<TableColumnsType<Record<string, React.ReactNode>>>(
            () =>
                (columns ?? []).map((column, index) => {
                    const isMappedColumn = index < 2 && ["key", "description"].includes(column.split("←")[0].trim().toLowerCase());
                    const isSchemaColumn = dataSchemaColumns.findIndex((item) => item.toLowerCase() === column.toLowerCase()) > -1;
                    const hasValidColumn = enableColumnMapping ? isMappedColumn : isSchemaColumn;

                    return {
                        key: column,
                        dataIndex: column,
                        title: (
                            <>
                                {column}
                                <FontAwesomeIcon icon={hasValidColumn ? faCheckCircle : faTimesCircle} className={`ml-1 ${hasValidColumn ? "text-green-600" : "text-red-600"}`} />
                            </>
                        ),
                    };
                }),
            [columns, dataSchemaColumns, enableColumnMapping],
        );

        const previewData = React.useMemo(
            () =>
                data
                    ? (data as Array<Record<string, React.ReactNode> | string[]>).map((row, index) => {
                          const values = Array.isArray(row) ? {} : { ...(row as Record<string, React.ReactNode>) };
                          return { ...values, __rowKey: index };
                      })
                    : undefined,
            [data],
        );

        const applyColumnMapping = React.useCallback(
            (previewRowsSource: PapaparseDataWithHeader, fullRowsSource: PapaparseDataWithHeader, rawCols: string[], srcFile: RcFile, keySel: string, descSel: string) => {
                // Upload file: only Key + Description columns (always clean header names)
                const uploadRows = fullRowsSource.map((row) => ({
                    Key: (row as Record<string, string>)[keySel] ?? "",
                    Description: (row as Record<string, string>)[descSel] ?? "",
                }));
                const csvString = Papaparse.unparse(uploadRows);
                const transformedFile = new File([csvString], srcFile.name, { type: "text/csv" }) as RcFile;

                // Preview display keys: add "← source" indicator when source differs from mapped name
                const keyDisplayKey = keySel === "Key" ? "Key" : `Key ← ${keySel}`;
                const descDisplayKey = descSel === "Description" ? "Description" : `Description ← ${descSel}`;

                // Remaining columns keep original names (including original "Key"/"Description" when not selected)
                const otherCols = rawCols.filter((col) => col !== keySel && col !== descSel);
                const previewCols = [keyDisplayKey, descDisplayKey, ...otherCols];
                const previewRows = previewRowsSource.map((row) => ({
                    [keyDisplayKey]: (row as Record<string, string>)[keySel] ?? "",
                    [descDisplayKey]: (row as Record<string, string>)[descSel] ?? "",
                    ...Object.fromEntries(otherCols.map((col) => [col, (row as Record<string, string>)[col]])),
                }));

                setData(previewRows as PapaparseDataWithHeader);
                setColumns(previewCols);

                // Callback: clean Key/Description keys so callers don't see display-only indicator names
                const callbackCols = ["Key", "Description", ...otherCols];
                const callbackRows = fullRowsSource.map((row) => ({
                    Key: (row as Record<string, string>)[keySel] ?? "",
                    Description: (row as Record<string, string>)[descSel] ?? "",
                    ...Object.fromEntries(otherCols.map((col) => [col, (row as Record<string, string>)[col]])),
                }));

                onReadFileComplete({
                    file: transformedFile,
                    data: callbackRows,
                    columns: callbackCols,
                    error: null,
                    meta: { fields: callbackCols },
                });
            },
            [onReadFileComplete],
        );

        const onFileReadError = React.useCallback(
            (uploadedFile: RcFile, error: string | null) => {
                setFile(uploadedFile);
                setColumns(null);
                setData(null);
                setIsReadingFile(false);
                onReadFileError &&
                    onReadFileError({
                        file: uploadedFile,
                        data: null,
                        columns: null,
                        error: error,
                        meta: null,
                    });
            },
            [onReadFileError],
        );

        const onFileReadComplete = React.useCallback(
            (uploadedFile: RcFile, results: PapaparseResult) => {
                if (results) {
                    const resultData = results.data;

                    //Remove last empty row.
                    removeTrailingEmptyRow(resultData);

                    const resultColumns = results.meta?.fields?.filter((field) => field) ?? null;
                    const resultMeta = results.meta ?? null;

                    if (enableColumnMapping) {
                        if (!resultColumns || resultColumns.length < 2) {
                            showError("The CSV must have at least 2 columns for Key and Description mapping.");
                            setIsReadingFile(false);
                            return;
                        }

                        // Auto-detect columns named "Key"/"Description" (case-insensitive); fall back to positional
                        const defaultKey = resultColumns.find((col) => col.toLowerCase() === "key") ?? resultColumns[0];
                        const defaultDesc =
                            resultColumns.find((col) => col.toLowerCase() === "description" && col !== defaultKey) ??
                            resultColumns.find((col) => col !== defaultKey) ??
                            resultColumns[1];

                        Papaparse.parse(uploadedFile, {
                            delimiter: ",",
                            header: hasHeader,
                            encoding: "UTF-8",
                            worker: true,
                            complete: (fullResult: PapaparseResult) => {
                                const fullResultData = fullResult.data;
                                removeTrailingEmptyRow(fullResultData);

                                setRawData(resultData as PapaparseDataWithHeader);
                                setFullRawData(fullResultData as PapaparseDataWithHeader);
                                setRawColumns(resultColumns);
                                setOriginalFile(uploadedFile);
                                setFile(uploadedFile);
                                setKeyCol(defaultKey);
                                setDescCol(defaultDesc);
                                setIsReadingFile(false);

                                applyColumnMapping(
                                    resultData as PapaparseDataWithHeader,
                                    fullResultData as PapaparseDataWithHeader,
                                    resultColumns,
                                    uploadedFile,
                                    defaultKey,
                                    defaultDesc,
                                );
                            },
                            error: (error: string | null) => onFileReadError(uploadedFile, error),
                        });
                    } else {
                        setFile(uploadedFile);
                        setColumns(resultColumns);
                        setData(resultData);
                        setIsReadingFile(false);
                        onReadFileComplete &&
                            onReadFileComplete({
                                file: uploadedFile,
                                data: resultData,
                                columns: resultColumns,
                                error: null,
                                meta: resultMeta,
                            });
                    }
                }
            },
            [enableColumnMapping, applyColumnMapping, hasHeader, onFileReadError, onReadFileComplete],
        );

        const onUploadFile = React.useCallback(
            (uploadedFile: RcFile) => {
                // Only accept CSV file for now
                const isCSV = uploadedFile.type === "text/csv" || uploadedFile.name.toLowerCase().endsWith(".csv");
                if (!isCSV) {
                    showError("Selected file is not a CSV file. Please try again.");
                    return;
                }

                setIsReadingFile(true);
                onReadingFile && onReadingFile();
                Papaparse.parse(uploadedFile, {
                    delimiter: ",",
                    header: hasHeader,
                    preview: 100,
                    encoding: "UTF-8",
                    worker: true,
                    complete: (result: PapaparseResult) => onFileReadComplete(uploadedFile, result),
                    error: (error: string | null) => onFileReadError(uploadedFile, error),
                });
                return false;
            },
            [hasHeader, onFileReadComplete, onFileReadError, onReadingFile],
        );

        const clearMappingState = React.useCallback(() => {
            setRawData(null);
            setFullRawData(null);
            setRawColumns(null);
            setOriginalFile(null);
            setKeyCol(null);
            setDescCol(null);
        }, []);

        const onFilePreviewClose = React.useCallback(() => {
            setFile(null);
            setData(null);
            setColumns(null);
            if (enableColumnMapping) {
                clearMappingState();
            }
            onCancelUpload && onCancelUpload();
        }, [enableColumnMapping, clearMappingState, onCancelUpload]);

        // Allow the parent to clear file in FileDragger
        React.useImperativeHandle(ref, () => ({
            clearFile() {
                setFile(null);
                setData(null);
                setColumns(null);
                if (enableColumnMapping) {
                    clearMappingState();
                }
            },
        }));

        return (
            <>
                <LoadingOverlay busy={isReadingFile} message={"Preparing preview..."} spinner />
                {data ? (
                    showPreview && (
                        <>
                            <DataTable<Record<string, React.ReactNode>>
                                header={
                                    <div className="flex w-full flex-col">
                                        <div className="flex w-full flex-row items-center justify-between">
                                            <Typography.Title level={3} className="!mb-0">
                                                Data Preview
                                            </Typography.Title>
                                        </div>
                                        {enableColumnMapping && rawColumns && rawColumns.length >= 2 && (
                                            <div className="flex flex-wrap items-center gap-4 rounded-md bg-slate-50 px-4 py-3">
                                                <span className="whitespace-nowrap text-sm font-semibold">Map CSV columns:</span>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm">Key ←</span>
                                                    <div data-testid="file-dragger-key-select">
                                                        <Select
                                                            style={{ minWidth: 160 }}
                                                            value={keyCol ?? undefined}
                                                            options={rawColumns.map((col) => ({ label: col, value: col }))}
                                                            onChange={(value: string) => {
                                                                setKeyCol(value);
                                                                if (rawData && fullRawData && rawColumns && originalFile && descCol) {
                                                                    applyColumnMapping(rawData, fullRawData, rawColumns, originalFile, value, descCol);
                                                                }
                                                            }}
                                                            virtual={false}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm">Description ←</span>
                                                    <div data-testid="file-dragger-description-select">
                                                        <Select
                                                            style={{ minWidth: 160 }}
                                                            value={descCol ?? undefined}
                                                            options={rawColumns.map((col) => ({ label: col, value: col }))}
                                                            onChange={(value: string) => {
                                                                setDescCol(value);
                                                                if (rawData && fullRawData && rawColumns && originalFile && keyCol) {
                                                                    applyColumnMapping(rawData, fullRawData, rawColumns, originalFile, keyCol, value);
                                                                }
                                                            }}
                                                            virtual={false}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                }
                                footer={
                                    <div className="flex w-full flex-row items-center justify-between gap-1 rounded-md bg-slate-50 px-4 py-3">
                                        <div className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                                            <Typography.Text type="secondary" title={file?.name ?? "No file name"}>
                                                File: {file?.name ?? "No file name"}
                                            </Typography.Text>
                                        </div>
                                        <Tooltip placement="topRight" title="Remove and upload another file">
                                            <Button
                                                danger
                                                onClick={onFilePreviewClose}
                                                aria-label="Remove and upload another file"
                                                type="primary"
                                                size="small"
                                                icon={<FontAwesomeIcon icon={faFileCircleMinus} />}
                                            >
                                                Remove File
                                            </Button>
                                        </Tooltip>
                                    </div>
                                }
                                containerClassName="size-full flex-1 gap-1"
                                headerClassName="!mb-0"
                                emptyText="No data found"
                                hideSelectionColumn={true}
                                tableProps={{
                                    dataSource: previewData,
                                    columns: previewColumns,
                                    rowKey: "__rowKey",
                                    pagination: {
                                        pageSize: 100,
                                    },
                                }}
                            />
                        </>
                    )
                ) : (
                    <Dragger
                        data-testid={CypressTestIds.MANAGE_BUSINESS_AREA_DATA_FILE_INPUT}
                        accept=".csv"
                        name="file"
                        multiple={false}
                        showUploadList={false}
                        disabled={disabled}
                        beforeUpload={onUploadFile}
                        className="flex-1"
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to this area to upload</p>
                        <p className="ant-upload-hint">Support for a singleupload.</p>
                    </Dragger>
                )}
            </>
        );
    },
);

export default FileDragger;
